/**
 * RiskHeatmap — site × severity matrix (v0.12.5).
 *
 * Aggregates active deviations + open CAPAs + open risks per site,
 * grouped by severity (CRITICAL / MAJOR / MINOR). Cell color saturates
 * with count.
 */

import { useRiskHeatmap } from '../../api/orgHooks';
import styles from './HomeAnalytics.module.css';

function intensity(count: number, max: number): number {
  if (max === 0) return 0;
  return Math.min(1, count / max);
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '185, 28, 28',  // red
  MAJOR: '146, 64, 14',     // amber
  MINOR: '22, 101, 52',     // green
};

export function RiskHeatmap() {
  const q = useRiskHeatmap();
  const d = q.data;
  if (!d || d.sites.length === 0) {
    return (
      <section className={styles.heatPanel} aria-label="Risk heatmap">
        <header className={styles.panelHead}>
          <h3 className={styles.panelTitle}>Risk heatmap · site × severity</h3>
        </header>
        <p className={styles.heatEmpty}>{q.isLoading ? 'Loading heatmap…' : 'No risk data.'}</p>
      </section>
    );
  }

  // Find the max cell value for color scaling.
  let max = 0;
  for (const site of d.sites) {
    for (const sev of d.severities) {
      const v = Number(d.matrix?.[site]?.[sev] || 0);
      if (v > max) max = v;
    }
  }
  if (max === 0) max = 1;

  return (
    <section className={styles.heatPanel} aria-label="Risk heatmap">
      <header className={styles.panelHead}>
        <h3 className={styles.panelTitle}>Risk heatmap · site × severity</h3>
        <span className={styles.panelSub}>Active deviations + open CAPAs + open risks per cell</span>
      </header>
      <div className={styles.heatGrid} style={{ gridTemplateColumns: `90px repeat(${d.sites.length}, minmax(0, 1fr))` }}>
        <div className={styles.heatCorner} />
        {d.sites.map((s) => (
          <div key={s} className={styles.heatColHead}>
            {s}
          </div>
        ))}
        {d.severities.map((sev) => (
          <>
            <div key={`row-${sev}`} className={styles.heatRowHead}>
              {sev}
            </div>
            {d.sites.map((s) => {
              const v = Number(d.matrix?.[s]?.[sev] || 0);
              const alpha = intensity(v, max) * 0.85 + 0.05;
              const bg = `rgba(${SEVERITY_COLORS[sev] ?? '107, 114, 128'}, ${alpha.toFixed(2)})`;
              const fg = alpha > 0.5 ? 'white' : 'var(--color-text-primary, #0A1023)';
              return (
                <div
                  key={`${sev}-${s}`}
                  className={styles.heatCell}
                  style={{ background: bg, color: fg }}
                  title={`${sev} at ${s}: ${v}`}
                >
                  {v}
                </div>
              );
            })}
          </>
        ))}
      </div>
    </section>
  );
}
